import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Stack,
    Stepper,
    Step,
    StepLabel,
    InputAdornment,
    IconButton,
    Divider,
    LinearProgress,
} from "@mui/material";
import {
    MailOutline as MailIcon,
    LockOutlined as LockIcon,
    Visibility,
    VisibilityOff,
    ArrowBack,
    CheckCircleOutline,
    ErrorOutline,
} from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";
import { Turnstile } from "@marsidev/react-turnstile";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";

class AuthRegister extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeStep: 0,
            name: "",
            email: "",
            captchaToken: "",
            code: "",
            password: "",
            confirmPassword: "",
            showPassword: false,
            showConfirmPassword: false,
            loading: false,
        };
    }

    // Regras de validação de senha forte
    getPasswordValidation = (password) => {
        return {
            hasMinLen: password.length >= 8,
            hasUpper: /[A-Z]/.test(password),
            hasLower: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };
    };

    isPasswordStrong = (password) => {
        const rules = this.getPasswordValidation(password);
        return Object.values(rules).every(Boolean);
    };

    // Alternar visibilidade da senha
    handleTogglePassword = () => {
        this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
    };

    handleToggleConfirmPassword = () => {
        this.setState((prevState) => ({ showConfirmPassword: !prevState.showConfirmPassword }));
    };

    // Função utilitária para extrair o nome do Token do Google
    parseJwt = (token) => {
        try {
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                    .join("")
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            return {};
        }
    };

    handleGoogleSuccess = async (credentialResponse) => {
        this.setState({ loading: true });
        const toastId = toast.loading("Autenticando via Google...");

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/oauth/google`, {
                credentials: "include",
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ credential: credentialResponse.credential }),
            });

            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                toast.dismiss(toastId);

                // Tenta extrair o nome retornado pela API ou do próprio token JWT do Google
                const userPayload = this.parseJwt(credentialResponse.credential);
                const name = data.user?.name || userPayload.name || userPayload.given_name || "Usuário";

                if (this.context && this.context.login) {
                    this.context.login(true);
                }

                // Exibe a tela de boas-vindas
                this.setState({ name, activeStep: 3 });
            } else {
                toast.update(toastId, {
                    render: data.message || "Erro ao realizar cadastro via Google.",
                    type: "error",
                    isLoading: false,
                    autoClose: 4000,
                });
            }
        } catch (err) {
            toast.update(toastId, {
                render: "Falha na conexão com o servidor.",
                type: "error",
                isLoading: false,
                autoClose: 4000,
            });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleNext = () => {
        this.setState((prevState) => ({ activeStep: prevState.activeStep + 1 }));
    };

    handleBack = () => {
        this.setState((prevState) => ({ activeStep: prevState.activeStep - 1 }));
    };

    handleChange = (e) => {
        const { id, value } = e.target;
        this.setState({ [id]: value });
    };

    // Passo 1: Enviar Email e validar Captcha
    sendEmailCode = async () => {
        const { name, email, captchaToken } = this.state;

        if (!name.trim()) {
            return toast.warning("Por favor, informe seu nome completo.");
        }

        this.setState({ loading: true });

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/register-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, captchaToken }),
            });
            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                toast.success("Código de verificação enviado para o seu e-mail!");
                this.handleNext();
            } else {
                toast.error(data.message || "Erro ao enviar e-mail. Tente novamente.");
            }
        } catch (error) {
            toast.error("Erro na conexão com o servidor.");
        } finally {
            this.setState({ loading: false });
        }
    };

    // Passo 2: Validar código
    verifyCode = async () => {
        const { email, code } = this.state;
        this.setState({ loading: true });

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, code }),
            });
            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                toast.success("E-mail verificado com sucesso!");
                this.handleNext();
            } else {
                toast.error(data.message || "Código inválido ou expirado.");
            }
        } catch (error) {
            toast.error("Erro na conexão com o servidor.");
        } finally {
            this.setState({ loading: false });
        }
    };

    // Passo 3: Finalizar Cadastro
    finalizeRegistration = async () => {
        const { name, email, password, confirmPassword } = this.state;

        if (password !== confirmPassword) {
            return toast.error("As senhas não coincidem.");
        }

        if (!this.isPasswordStrong(password)) {
            return toast.error("A senha precisa atender a todos os requisitos de segurança.");
        }

        this.setState({ loading: true });

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/complete-register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, password }),
            });
            const data = await res.json().catch(() => ({}));

            if (res.status === 201 || res.ok) {
                this.setState({ activeStep: 3 });
            } else {
                toast.error(data.message || "Erro ao concluir cadastro. Tente novamente.");
            }
        } catch (error) {
            toast.error("Erro de comunicação com o servidor.");
        } finally {
            this.setState({ loading: false });
        }
    };

    renderPasswordRequirements(password) {
        const rules = this.getPasswordValidation(password);
        const requirements = [
            { label: "Pelo menos 8 caracteres", valid: rules.hasMinLen },
            { label: "Uma letra maiúscula (A-Z)", valid: rules.hasUpper },
            { label: "Uma letra minúscula (a-z)", valid: rules.hasLower },
            { label: "Um número (0-9)", valid: rules.hasNumber },
            { label: "Um caractere especial (!@#$%...)", valid: rules.hasSpecial },
        ];

        return (
            <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                    Sua senha deve conter:
                </Typography>
                <Stack spacing={0.5}>
                    {requirements.map((req, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            {req.valid ? (
                                <CheckCircleOutline color="success" sx={{ fontSize: 14 }} />
                            ) : (
                                <ErrorOutline color="action" sx={{ fontSize: 14 }} />
                            )}
                            <Typography variant="caption" color={req.valid ? "success.main" : "text.secondary"}>
                                {req.label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    }

    renderStepContent(step) {
        const {
            name,
            email,
            code,
            password,
            confirmPassword,
            captchaToken,
            showPassword,
            showConfirmPassword,
            loading,
        } = this.state;

        switch (step) {
            case 0:
                return (
                    <Stack spacing={2.5}>
                        <TextField
                            fullWidth
                            id="name"
                            label="Nome Completo"
                            type="text"
                            value={name}
                            onChange={this.handleChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            id="email"
                            label="E-mail"
                            type="email"
                            value={email}
                            onChange={this.handleChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MailIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                            <Turnstile
                                siteKey="0x4AAAAAADD5XelpNdivqvNc"
                                onSuccess={(token) => this.setState({ captchaToken: token })}
                            />
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!captchaToken || !email || !name || loading}
                            onClick={this.sendEmailCode}
                            sx={{ py: 1.5, borderRadius: 3, fontWeight: "bold" }}
                        >
                            {loading ? "Enviando..." : "Enviar Código"}
                        </Button>
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Enviamos um código de verificação para <b>{email}</b>.
                        </Typography>
                        <TextField
                            fullWidth
                            id="code"
                            label="Código de Verificação"
                            placeholder="000000"
                            value={code}
                            onChange={this.handleChange}
                            inputProps={{
                                maxLength: 6,
                                style: { textAlign: "center", letterSpacing: "8px", fontSize: "20px" },
                            }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={code.length < 6 || loading}
                            onClick={this.verifyCode}
                            sx={{ py: 1.5, borderRadius: 3, fontWeight: "bold" }}
                        >
                            {loading ? "Verificando..." : "Verificar Código"}
                        </Button>
                        <Button variant="text" size="small" onClick={this.handleBack} disabled={loading}>
                            Alterar E-mail
                        </Button>
                    </Stack>
                );
            case 2:
                const isStrong = this.isPasswordStrong(password);
                const isMatch = password === confirmPassword && confirmPassword !== "";

                return (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Crie uma senha forte para proteger sua conta.
                        </Typography>
                        <TextField
                            fullWidth
                            id="password"
                            label="Nova Senha"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={this.handleChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={this.handleTogglePassword} edge="end">
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {password.length > 0 && this.renderPasswordRequirements(password)}

                        <TextField
                            fullWidth
                            id="confirmPassword"
                            label="Confirmar Senha"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={this.handleChange}
                            error={confirmPassword !== "" && !isMatch}
                            helperText={confirmPassword !== "" && !isMatch ? "As senhas não coincidem" : ""}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={this.handleToggleConfirmPassword} edge="end">
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!isStrong || !isMatch || loading}
                            onClick={this.finalizeRegistration}
                            sx={{ py: 1.5, borderRadius: 3, mt: 1, fontWeight: "bold" }}
                        >
                            {loading ? "Finalizando..." : "Concluir Cadastro"}
                        </Button>
                    </Stack>
                );
            case 3:
                const firstName = name ? name.split(" ")[0] : "Bem-vindo";

                return (
                    <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
                        <CheckCircleOutline color="success" sx={{ fontSize: 68 }} />
                        <Typography variant="h5" fontWeight="bold" textAlign="center" color="primary">
                            Seja bem-vindo(a), {firstName}!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Sua conta foi vinculada e cadastrada com sucesso. Aproveite todos os recursos da nossa plataforma!
                        </Typography>
                        <Button
                            fullWidth
                            component={Link}
                            to="/auth/login"
                            variant="contained"
                            size="large"
                            sx={{ py: 1.5, borderRadius: 3, fontWeight: "bold" }}
                        >
                            Acessar Minha Conta
                        </Button>
                    </Stack>
                );
            default:
                return null;
        }
    }

    render() {
        const { activeStep, loading } = this.state;
        const steps = ["E-mail", "Verificação", "Senha"];

        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    p: 2,
                }}
            >
                <Container maxWidth="xs">
                    <Paper elevation={6} sx={{ p: 4, borderRadius: 4, position: "relative", overflow: "hidden" }}>
                        {loading && (
                            <LinearProgress
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                }}
                            />
                        )}

                        {activeStep < 3 && (
                            <>
                                <Box sx={{ textAlign: "center", mb: 3 }}>
                                    <Typography variant="h5" fontWeight="bold">
                                        Criar Sua Conta
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Preencha as informações para começar
                                    </Typography>
                                </Box>

                                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                                    {steps.map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>

                                {activeStep === 0 && (
                                    <>
                                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                                            <GoogleOAuthProvider clientId="992901332964-450q4r6gduuii5fo9be5ec8hnbk0rk56.apps.googleusercontent.com">
                                                <GoogleLogin
                                                    onSuccess={this.handleGoogleSuccess}
                                                    onError={() => toast.error("Falha na autenticação via Google.")}
                                                    useOneTap
                                                    theme="filled_blue"
                                                    width="100%"
                                                />
                                            </GoogleOAuthProvider>
                                        </Box>
                                        <Divider sx={{ my: 2 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                                OU CONTINUE COM EMAIL
                                            </Typography>
                                        </Divider>
                                    </>
                                )}
                            </>
                        )}

                        {this.renderStepContent(activeStep)}

                        {activeStep < 3 && (
                            <Box sx={{ mt: 3, textAlign: "center" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Já tem uma conta?{" "}
                                    <Button
                                        component={Link}
                                        to="/auth/login"
                                        sx={{ fontWeight: "bold", p: 0, minWidth: "auto" }}
                                    >
                                        Entrar
                                    </Button>
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                        <Button component={Link} to="/" startIcon={<ArrowBack />} color="inherit" sx={{ opacity: 0.7 }}>
                            Voltar para início
                        </Button>
                    </Box>
                </Container>
            </Box>
        );
    }
}

export default AuthRegister;